// formatTripDateRange — pure formatting of the trip's start/end date range label.
//
// Extracted verbatim from `EditTripModal.js` (the IIFE in the "Trip Info" header
// that renders `${formatDate(startDate)} - ${formatDate(endDate)}`) as part of
// the behavior-preserving refactor (issue #240). Each bound is parsed with
// `new Date(...)` and formatted as `MM/DD/YY` (zero-padded month/day, last two
// digits of the year); an unparseable bound formats as `'Invalid Date'`. The
// inner `formatDate` helper is kept inline exactly as the source had it.
//
// Totality preserved exactly: the source wrapped the whole computation in a
// `try/catch` that, on any thrown error, fell back to the raw
// `${trip.startDate} - ${trip.endDate}` string — that guard and fallback are
// reproduced verbatim, including the `isNaN(date.getTime())` "Invalid Date"
// branch. The function reads only `trip.startDate` / `trip.endDate`.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

/**
 * Formats the trip's start/end dates as an `MM/DD/YY - MM/DD/YY` label.
 *
 * @param {Object} trip - The trip whose `startDate`/`endDate` are formatted.
 * @returns {string} The formatted date range, or the raw values on parse failure.
 */
export function formatTripDateRange(trip) {
  try {
    const startDate = new Date(trip.startDate);
    const endDate = new Date(trip.endDate);
    const formatDate = (date) => {
      if (isNaN(date.getTime())) return 'Invalid Date';
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getDate().toString().padStart(2, '0')}/${date.getFullYear().toString().slice(-2)}`;
    };
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  } catch (error) {
    return `${trip.startDate} - ${trip.endDate}`;
  }
}

export default formatTripDateRange;
